import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Phone, Calendar, Filter, X, Eye, Share2 } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { ClinicVideo } from '@/components/ClinicVideo'
import '@/styles/animations.css'

const categories = [
  'All',
  'Before & After',
  'Smile Makeovers',
  'Dental Procedures',
  'Clinic Interior',
  'Team & Events',
  'Patient Care',
  'Technology'
]

const galleryItems = [
  {
    id: 1,
    title: 'Smile Makeover - Teeth Whitening',
    description: 'Professional teeth whitening treatment showing dramatic results',
    category: 'Before & After',
    tags: ['teeth whitening', 'cosmetic dentistry', 'smile makeover'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: true
  },
  {
    id: 2,
    title: 'Dental Crown Restoration',
    description: 'Complete crown restoration showing natural-looking results',
    category: 'Before & After',
    tags: ['dental crown', 'restoration', 'cosmetic dentistry'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 3,
    title: 'Modern Clinic Interior',
    description: 'Our state-of-the-art dental clinic with modern equipment',
    category: 'Clinic Interior',
    tags: ['clinic', 'interior', 'modern equipment'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: true
  },
  {
    id: 4,
    title: 'Root Canal Treatment Process',
    description: 'Step-by-step root canal treatment procedure',
    category: 'Dental Procedures',
    tags: ['root canal', 'endodontics', 'treatment'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 5,
    title: 'Orthodontic Treatment Results',
    description: 'Beautiful straight teeth after orthodontic treatment',
    category: 'Before & After',
    tags: ['orthodontics', 'braces', 'straight teeth'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: true
  },
  {
    id: 6,
    title: 'Pediatric Dental Care',
    description: 'Gentle dental care for children in a comfortable environment',
    category: 'Patient Care',
    tags: ['pediatric', 'children', 'dental care'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 7,
    title: 'Advanced Dental Equipment',
    description: 'Latest dental technology for precise treatments',
    category: 'Technology',
    tags: ['technology', 'equipment', 'advanced'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 8,
    title: 'Complete Smile Transformation',
    description: 'Comprehensive smile makeover with multiple procedures',
    category: 'Smile Makeovers',
    tags: ['smile makeover', 'transformation', 'cosmetic dentistry'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: true
  },
  {
    id: 9,
    title: 'Dr. Aishwarya with Patient',
    description: 'Dr. Aishwarya providing gentle care to a patient',
    category: 'Team & Events',
    tags: ['doctor', 'patient care', 'consultation'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 10,
    title: 'Dental Implant Success',
    description: 'Natural-looking dental implant restoration',
    category: 'Before & After',
    tags: ['dental implant', 'restoration', 'missing teeth'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 11,
    title: 'Sterilization Area',
    description: 'Our advanced sterilization and infection control area',
    category: 'Clinic Interior',
    tags: ['sterilization', 'safety', 'infection control'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  },
  {
    id: 12,
    title: 'Patient Education Session',
    description: 'Dr. Aishwarya educating patients about oral health',
    category: 'Patient Care',
    tags: ['education', 'patient care', 'oral health'],
    type: 'image',
    thumbnail: '/api/placeholder/300/200',
    fullSize: '/api/placeholder/800/600',
    featured: false
  }
]

export function GalleryPage() {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedItem, setSelectedItem] = useState<typeof galleryItems[0] | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const filteredItems = selectedCategory === 'All' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory)

  const featuredItems = galleryItems.filter(item => item.featured)

  const openLightbox = (item: typeof galleryItems[0]) => {
    setSelectedItem(item)
  }

  const closeLightbox = () => {
    setSelectedItem(null)
  }

  return (
    <div className="min-h-screen">
      <Header currentPage="gallery" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              Our <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">Gallery</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Explore our dental transformations, modern clinic facilities, and the caring environment we provide for all our patients
            </p>
          </div>
        </div>
      </section>

      {/* Featured Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Transformations</h2>
            <p className="text-xl text-gray-600">Showcasing our best work and patient success stories</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredItems.map((item) => (
              <div key={item.id} className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="aspect-w-16 aspect-h-10 bg-gray-200">
                  <div className="w-full h-64 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">Image Placeholder</span>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openLightbox(item)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </Button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                  <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                  <p className="text-gray-200 text-sm mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinic Video */}
      <ClinicVideo heading="Take a Tour" subheading="See our clinic, our care and our team in two minutes - watch in English or Tamil" />

      {/* Gallery Filters */}
      <section id="gallery" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">Complete Gallery</h2>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>

          {/* Filter Categories */}
          <div className={`mb-8 ${showFilters ? 'block' : 'hidden md:block'}`}>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div key={item.id} className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="aspect-w-1 aspect-h-1 bg-gray-200">
                  <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    <span className="text-gray-500 text-xs">Image Placeholder</span>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openLightbox(item)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-4">
                  <h3 className="text-white font-medium text-sm">{item.title}</h3>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.tags.slice(0, 2).map((tag, index) => (
                      <span key={index} className="bg-blue-500 bg-opacity-80 text-white text-xs px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 hover:bg-gray-100"
              >
                <X className="h-6 w-6" />
              </button>
              <div className="aspect-w-16 aspect-h-10 bg-gray-200 rounded-t-lg">
                <div className="w-full h-96 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center rounded-t-lg">
                  <span className="text-gray-500">Full Size Image Placeholder</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedItem.title}</h3>
                <p className="text-gray-600 mb-4">{selectedItem.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedItem.tags.map((tag, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Category: {selectedItem.category}</span>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm">
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </Button>
                    <Button size="sm">
                      <Calendar className="mr-2 h-4 w-4" />
                      Book Consultation
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl font-bold mb-4 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>Ready for Your Transformation?</h2>
          <p className="text-xl mb-8 text-gray-300 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
            Schedule a consultation with Dr. Aishwarya V to discuss your dental goals and create your personalized treatment plan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
            <Button asChild size="lg" className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl">
              <Link to="/book-appointment">
                <Calendar className="mr-2 h-5 w-5" />
                Book Consultation
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-yellow-400 text-white bg-yellow-400/20 hover:bg-yellow-400 hover:text-black transform hover:scale-105 transition-all duration-300 shadow-lg">
              <a href="tel:+91-9841817510">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}